import { Injectable } from '@angular/core';
import { AjaxResponse } from 'rxjs/ajax';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import type { EntityId } from 'src/app/shared/config/store-config';
import { BaseRequester } from '../common/base-requester';
import {
    DeleteLastOutputTaxRequest,
    DeleteOutputTaxRequest,
    SaveOutputTaxRequest,
    OutputTax,
    GetOutputTaxRequest,
    SaveOutputTaxSharedUrlRequest,
} from './interfaces/output-tax-request';
@Injectable({
    providedIn: 'root',
})
export class OutputTaxService extends BaseRequester {
    constructor() {
        super();
    }

    public saveTaxInvoice(parameter: OutputTax): Observable<any> {
        return this.request<SaveOutputTaxRequest>({
            operation: 'save',
            parameter,
        });
    }

    public saveTaxInvoiceSharedUrl(invNum: string, fileSharedLink: string) {
        return this.request<SaveOutputTaxSharedUrlRequest>({
            operation: 'saveInvoiceSharedUrl',
            parameter: {
                invNum,
                fileSharedLink,
            },
        });
    }

    // Soft void (spec W4/D2): the row and its number are RETAINED with a
    // cancellation marker — the wire operation is still called 'delete' for
    // compatibility, but nothing is deleted. The reason prints against the
    // cancelled number in the report.
    public voidTaxInvoice(invNum: string, voidReason: string): Observable<any> {
        return this.request<DeleteOutputTaxRequest>({
            operation: 'delete',
            parameter: { invNum, voidReason },
        });
    }

    // HARD delete — permanently removes the row and its items and steps the
    // series counter back, so the number is minted again by the next save.
    // The Lambda accepts it only for the LAST number of its series and
    // rejects anything earlier (void those instead).
    public deleteLastTaxInvoice(invNum: string): Observable<any> {
        return this.request<DeleteLastOutputTaxRequest>({
            operation: 'deleteLast',
            parameter: { invNum },
        });
    }

    public getOutputTax(
        year: number,
        month: number,
        entityId?: EntityId,
    ): Observable<OutputTax[]> {
        return this.request<GetOutputTaxRequest>({
            operation: 'get',
            parameter: {
                year,
                month,
                ...(entityId !== undefined ? { entityId } : {}),
            },
        });
    }

    private request<T>(request: T): Observable<any> {
        return this.internalPostRequest(
            environment.outputTaxEndpoint,
            request,
        ).pipe(
            map((response: AjaxResponse) => {
                if (!response.response.errorMessage) {
                    return response.response;
                } else {
                    throw new Error(
                        response.response.errorMessage || response.response,
                    );
                }
            }),
        );
    }
}
